import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Download, Loader2, MessageSquareWarning, Scale } from 'lucide-react';
import { SignApproveForm } from '../components/approval/SignApproveForm';

const API = process.env.REACT_APP_BACKEND_URL;
const call = async (path, opts) => {
  const r = await fetch(`${API}/api${path}`, { ...opts, headers: { 'Content-Type': 'application/json' } });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || 'No se pudo completar');
  return d;
};

// Public page where the client approves a brief or asks for changes (Fidem Presenta)
export default function ApprovalPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(null);
  const [mode, setMode] = useState(params.get('decision') === 'aprobar' ? 'approve' : null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { call(`/approval/${token}`).then(setA).catch((e) => setErr(e.message)); }, [token]);
  const answer = async (decision, extra = {}) => {
    setBusy(true);
    setErr(null);
    try { const d = await call(`/approval/${token}`, { method: 'POST', body: JSON.stringify({ decision, comment, ...extra }) }); setA({ ...a, ...d, status: decision, comment }); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  if (err && !a) return <div data-testid="approval-error" className="flex min-h-screen items-center justify-center bg-slate-100 p-6 text-slate-700">{err}</div>;
  if (!a) return <div className="flex min-h-screen items-center justify-center bg-slate-100"><Loader2 className="h-6 w-6 animate-spin text-slate-500" /></div>;
  return (
    <div data-testid="approval-page" className="min-h-screen bg-slate-100 px-4 py-10 text-slate-900">
      <div className="mx-auto max-w-2xl rounded-2xl bg-white p-6 shadow-sm sm:p-10">
        <p className="flex items-center gap-2 text-sm font-semibold text-slate-500"><Scale className="h-4 w-4" />NotaryOS · visto bueno del cliente</p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">{a.matter_title}</h1>
        <p className="mt-2 text-sm text-slate-600">{a.sender} te envió este escrito para que lo apruebes antes de presentarlo.</p>
        {a.suma && <p data-testid="approval-suma" className="mt-4 rounded-lg bg-slate-50 p-3 font-serif text-sm">{a.suma}</p>}
        <div className="mt-4 flex flex-wrap gap-2">
          <a data-testid="approval-pdf" href={`${API}${a.pdf_url}`} className="flex items-center gap-1 rounded-full bg-slate-900 px-4 py-2 text-sm text-white"><Download className="h-4 w-4" />Leer el escrito (PDF)</a>
          <a data-testid="approval-docx" href={`${API}${a.docx_url}`} className="flex items-center gap-1 rounded-full border border-slate-300 px-4 py-2 text-sm"><Download className="h-4 w-4" />Word</a>
          {a.annex_url && <a data-testid="approval-annex" href={`${API}${a.annex_url}`} className="flex items-center gap-1 rounded-full border border-amber-500 px-4 py-2 text-sm text-amber-800"><Download className="h-4 w-4" />Informe estratégico (confidencial)</a>}
        </div>
        {a.status !== 'pending' ? (
          <div data-testid="approval-done" className={`mt-8 rounded-xl p-4 text-sm ${a.status === 'approved' ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
            {a.status === 'approved' ? (
              <>
                <p className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 shrink-0" />Aprobaste y firmaste el escrito{a.signer_name ? ` como ${a.signer_name} (RUT ${a.signer_rut})` : ''}. Avisamos a tu abogado.</p>
                {a.signer_name && <a data-testid="approval-signed-pdf" href={`${API}/api/approval/${token}/signed.pdf`} className="mt-3 inline-flex items-center gap-1 rounded-full bg-emerald-700 px-4 py-2 text-white"><Download className="h-4 w-4" />Descargar escrito firmado (PDF)</a>}
              </>
            ) : <p className="flex items-center gap-2"><MessageSquareWarning className="h-5 w-5" />Pediste cambios: «{a.comment}». Tu abogado ya fue avisado.</p>}
          </div>
        ) : (
          <div className="mt-8 space-y-3">
            {mode === 'approve' && <SignApproveForm busy={busy} onSign={(f) => answer('approved', f)} onBack={() => setMode(null)} />}
            {!mode && <button data-testid="approval-approve-btn" onClick={() => setMode('approve')} className="w-full rounded-xl bg-emerald-600 py-3 font-semibold text-white">Aprobar y firmar escrito</button>}
            {!mode && <button data-testid="approval-changes-btn" onClick={() => setMode('changes')} className="w-full rounded-xl border border-slate-300 py-3 text-sm">Pedir cambios</button>}
            {mode === 'changes' && (
              <>
                <textarea data-testid="approval-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={5} placeholder="¿Qué quieres que cambie tu abogado?" className="w-full rounded-xl border border-slate-300 p-3 text-sm" />
                <button data-testid="approval-send-changes-btn" disabled={busy || comment.trim().length < 5} onClick={() => answer('changes')} className="w-full rounded-xl bg-amber-500 py-3 font-semibold text-slate-950 disabled:opacity-50">Enviar cambios a mi abogado</button>
                <button onClick={() => setMode(null)} className="w-full text-xs text-slate-500">Volver</button>
              </>
            )}
            {err && <p data-testid="approval-answer-error" className="text-sm text-red-600">{err}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
